import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../api';

// Передача позиции курьера из вкладки АРМа (Geolocation API), MVP без приложения.
// Включается автоматически после разового согласия и работает, пока вкладка
// открыта: iOS Safari замораживает фон — позиция обновится при возврате из
// Навигатора, то есть на каждой точке. Wake Lock не даёт экрану погаснуть.
// Ничего «завершать» не нужно: сервер сам скрывает позиции старше 12 ч и удаляет
// старше суток; «старт дня» он же ставит по первой позиции.

const MIN_INTERVAL_MS = 30000; // не чаще раза в 30 с…
const MIN_DISTANCE_M = 50;     // …если не сдвинулись на 50 м

function distanceM(a, b) {
  const r = 6371000;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLon = ((b.lon - a.lon) * Math.PI) / 180;
  const x = Math.sin(dLat / 2) ** 2
    + Math.cos((a.lat * Math.PI) / 180) * Math.cos((b.lat * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
  return 2 * r * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
}

export default function useCourierGeolocation({ onError } = {}) {
  const supported = typeof navigator !== 'undefined' && 'geolocation' in navigator;
  const [active, setActive] = useState(false);
  const [lastSent, setLastSent] = useState(null);
  const [error, setError] = useState(null);
  const watchRef = useRef(null);
  const lastRef = useRef(null);
  const wakeRef = useRef(null);
  const onErrorRef = useRef(onError);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);

  const releaseWake = () => {
    if (wakeRef.current) {
      try { wakeRef.current.release(); } catch { /* noop */ }
      wakeRef.current = null;
    }
  };

  const requestWake = async () => {
    if (!navigator.wakeLock || wakeRef.current) return;
    try { wakeRef.current = await navigator.wakeLock.request('screen'); } catch { /* запрещено — не критично */ }
  };

  const stopWatching = useCallback(() => {
    if (watchRef.current != null && supported) {
      navigator.geolocation.clearWatch(watchRef.current);
      watchRef.current = null;
    }
    releaseWake();
  }, [supported]);

  const send = useCallback(async (pos) => {
    const c = pos.coords;
    const point = { lat: c.latitude, lon: c.longitude };
    const prev = lastRef.current;
    if (prev && Date.now() - prev.t < MIN_INTERVAL_MS && distanceM(prev, point) < MIN_DISTANCE_M) {
      return;
    }
    try {
      await api.postMyLocation({
        lat: c.latitude,
        lon: c.longitude,
        accuracy_m: Number.isFinite(c.accuracy) ? c.accuracy : null,
        heading: Number.isFinite(c.heading) ? c.heading : null,
        speed_mps: Number.isFinite(c.speed) ? c.speed : null,
        recorded_at: new Date(pos.timestamp || Date.now()).toISOString(),
      });
      lastRef.current = { ...point, t: Date.now() };
      setLastSent(lastRef.current);
      setError(null);
    } catch (e) {
      const msg = String(e?.message || e);
      if (e?.status === 403 || /403|соглас/i.test(msg)) {
        setError('consent');
        onErrorRef.current?.('consent');
        stopWatching();
        setActive(false);
      } else {
        setError('send');
      }
    }
  }, [stopWatching]);

  const start = useCallback(() => {
    if (!supported) { setError('unsupported'); onErrorRef.current?.('unsupported'); return; }
    if (watchRef.current != null) return;
    setActive(true);
    setError(null);
    watchRef.current = navigator.geolocation.watchPosition(
      (pos) => { send(pos); },
      (err) => {
        console.warn('[geolocation]', err);
        setError(err.code === 1 ? 'denied' : 'position');
        if (err.code === 1) onErrorRef.current?.('denied');
      },
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 20000 },
    );
    requestWake();
  }, [supported, send]);

  const stop = useCallback(() => {
    stopWatching();
    setActive(false);
  }, [stopWatching]);

  // Wake Lock отпускается при уходе в фон — берём заново, когда вкладка снова видима
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible' && watchRef.current != null) requestWake(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      stopWatching();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { supported, active, start, stop, lastSent, error };
}
